/**
 * deviceStore — lokaler (non-roaming) Speicher fuer die E2E-Geraete-Creds (#1537).
 *
 * Haelt pro Entra-Account ein { device_id, device_secret }. Das device_secret ist
 * der einzige client-seitige Anker fuer den geraete-gebundenen Master-Zugriff
 * (siehe KeyService::loadMasterViaDevice im Backend). Es verlaesst dieses Geraet
 * nur als Request-Header (X-FD-Device-Secret) an die FlawDesk-API und wird NIE
 * serverseitig at-rest gehalten -- so bleibt das Zero-Knowledge-Modell (#1170)
 * erhalten.
 *
 * IndexedDB statt Office.js RoamingSettings: Roaming wuerde das Secret ueber das
 * Postfach synchronisieren -- bewusst NICHT gewollt. IndexedDB ist origin-lokal
 * und uebersteht Add-in-Reloads beim Mail-Wechsel.
 *
 * Alle Operationen sind best-effort: Aufrufer behandeln Fehler als "keine Creds".
 */

const DB_NAME = 'flawdesk-outlook'
const DB_VERSION = 1
const STORE = 'device_creds'

function openDb() {
  return new Promise((resolve, reject) => {
    let req
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION)
    } catch (e) {
      reject(e)
      return
    }
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        // Out-of-line keys: der accountKey (MSAL homeAccountId) ist der Schluessel.
        db.createObjectStore(STORE)
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

/**
 * Liest die Geraete-Creds fuer einen Account. Liefert { device_id, device_secret }
 * oder null (auch bei fehlendem/teilweisem Eintrag oder IndexedDB-Fehler).
 */
export async function getDeviceCreds(accountKey) {
  if (!accountKey) return null
  let db
  try {
    db = await openDb()
  } catch {
    return null
  }
  try {
    return await new Promise((resolve, reject) => {
      const t = db.transaction(STORE, 'readonly')
      const req = t.objectStore(STORE).get(accountKey)
      req.onsuccess = () => {
        const v = req.result
        if (v && v.device_id && v.device_secret) {
          resolve({ device_id: v.device_id, device_secret: v.device_secret })
        } else {
          resolve(null)
        }
      }
      req.onerror = () => reject(req.error)
    })
  } catch {
    return null
  } finally {
    db.close()
  }
}

/** Speichert die Geraete-Creds fuer einen Account. No-op bei unvollstaendigen Daten. */
export async function setDeviceCreds(accountKey, creds) {
  if (!accountKey || !creds || !creds.device_id || !creds.device_secret) return
  const db = await openDb()
  try {
    await new Promise((resolve, reject) => {
      const t = db.transaction(STORE, 'readwrite')
      t.objectStore(STORE).put(
        { device_id: creds.device_id, device_secret: creds.device_secret },
        accountKey,
      )
      t.oncomplete = () => resolve()
      t.onerror = () => reject(t.error)
      t.onabort = () => reject(t.error)
    })
  } finally {
    db.close()
  }
}

/** Loescht die Geraete-Creds eines Accounts (z.B. wenn das Geraet serverseitig widerrufen wurde). */
export async function clearDeviceCreds(accountKey) {
  if (!accountKey) return
  let db
  try {
    db = await openDb()
  } catch {
    return
  }
  try {
    await new Promise((resolve, reject) => {
      const t = db.transaction(STORE, 'readwrite')
      t.objectStore(STORE).delete(accountKey)
      t.oncomplete = () => resolve()
      t.onerror = () => reject(t.error)
      t.onabort = () => reject(t.error)
    })
  } finally {
    db.close()
  }
}
